import { Inject, Injectable } from "@angular/core";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { localize } from "@gtmhub/localization";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { isCurrentUserOwner } from "@gtmhub/sessions/utils/granular-permission.util";
import { ItemAction } from "@gtmhub/shared/components/ui/models";
import { CurrentUserService } from "@gtmhub/users";
import { DropdownMenuItem } from "@webapp/shared/dropdown/dropdown.models";
import { DropdownMenuItemBuilder } from "@webapp/shared/dropdown/util/dropdown-menu-item-builder";
import { canCascadeMetric, isOkrAbandoned } from "../../utils/utils";
import { IMetricContributorActionHandlers, IMetricContributorContext } from "../models/metric-contributor-dropdown.models";

@Injectable({
  providedIn: "any",
})
export class MetricContributorDropdownFacade {
  constructor(private accountService: AccountService, private currentUserService: CurrentUserService, @Inject("$rootScope") private rootScope: IGtmhubRootScopeService) {}
  private context: IMetricContributorContext;
  private actionHandlers: IMetricContributorActionHandlers;
  private isA14UpdateKeyResultEnabled: boolean;
  private isCascadingShouldDecreaseToEnabled: boolean;

  public setContextAndActions(
    context: IMetricContributorContext,
    actionHandlers: IMetricContributorActionHandlers,
    featureFlags?: {
      a14UpdateKeyResult: boolean;
      isCascadingShouldDecreaseToEnabled: boolean;
    }
  ): MetricContributorDropdownFacade {
    this.context = context;
    this.actionHandlers = actionHandlers;
    this.isA14UpdateKeyResultEnabled = featureFlags?.a14UpdateKeyResult;
    this.isCascadingShouldDecreaseToEnabled = featureFlags?.isCascadingShouldDecreaseToEnabled;
    return this;
  }

  public getItems(): DropdownMenuItem[] {
    const items: DropdownMenuItem[] = [];
    const map = this.constructMap();
    for (const [localizationKey, itemData] of map) {
      if (itemData.shouldBeIncluded) {
        const menuItemBuilder = new DropdownMenuItemBuilder();
        menuItemBuilder.setKey(localize(localizationKey)).setAction(itemData.action);

        if (itemData.isEndOfSection) {
          menuItemBuilder.setToBeEndOfSection();
        }

        if (itemData.isDestructive) {
          menuItemBuilder.setToBeDestructive();
        }

        if (itemData.skipFromTabOrder) {
          menuItemBuilder.setSkipFromTabOrder();
        }

        menuItemBuilder.setTrack(itemData.track).setTrackMetaData(itemData.trackMetaData);
        items.push(menuItemBuilder.build());
      }
    }

    return items;
  }

  private constructMap(): Map<
    string,
    {
      shouldBeIncluded: boolean;
      action: ItemAction;
      isEndOfSection: boolean;
      isDestructive: boolean;
      track?: string;
      trackMetaData?: { [key: string]: string };
      skipFromTabOrder?: boolean;
    }
  > {
    return new Map([
      [
        "update",
        {
          shouldBeIncluded: this.canUpdateMetric(),
          action: this.getUpdateActionConfig(),
          isEndOfSection: false,
          isDestructive: false,
        },
      ],
      [
        "edit",
        {
          shouldBeIncluded: this.canEditMetric(),
          action: {
            state: this.context.metric.insightName === "" ? "._editMetric.editMetric" : "._editMetric.editDynamicMetric",
            params: { id: this.context.goal.id, metricId: this.context.metric.id },
          },
          isEndOfSection: false,
          isDestructive: false,
        },
      ],
      [
        "details",
        {
          shouldBeIncluded: true,
          action: { state: "._metric.metric", params: { metricId: this.context.metric.id } },
          isEndOfSection: true,
          isDestructive: false,
          skipFromTabOrder: true,
        },
      ],
      [
        "add_child_goal",
        {
          shouldBeIncluded: this.canAddChildGoal(),
          action: {
            state: "._goalForm.form",
            params: {
              id: this.context.goal.id,
              parentId: this.context.metric.id,
              parentType: "metric",
              externalSessionId: this.context.metric.sessionId || this.context.goal.sessionId,
            },
          },
          isEndOfSection: !this.canCascade(),
          isDestructive: false,
          skipFromTabOrder: true,
        },
      ],
      [
        "confirm_cascade",
        {
          shouldBeIncluded: this.canCascade(),
          action: { handler: (): void => this.actionHandlers.cascade(this.context.metric, this.context.goal.name) },
          isEndOfSection: true,
          isDestructive: false,
          track: "Button Clicked",
          trackMetaData: { button_name: "cascade", item_id: this.context.metric.id },
          skipFromTabOrder: true,
        },
      ],
      [
        "move",
        {
          shouldBeIncluded: this.canMove(),
          action: { handler: (): void => this.actionHandlers.move(this.context.metric) },
          isEndOfSection: this.context.session.status !== "open" && this.context.goal.locked,
          isDestructive: false,
          skipFromTabOrder: true,
        },
      ],
      [
        "clone",
        {
          shouldBeIncluded: this.canClone(),
          action: { handler: (): void => this.actionHandlers.clone(this.context.metric) },
          isEndOfSection: !this.canDisableProgressSync(),
          isDestructive: false,
          skipFromTabOrder: true,
        },
      ],
      [
        "disable_progress_sync",
        {
          shouldBeIncluded: this.canDisableProgressSync(),
          action: { handler: (): void => this.actionHandlers.disableSync(this.context.metric.id) },
          isEndOfSection: true,
          isDestructive: false,
          skipFromTabOrder: true,
        },
      ],
      [
        "copy_url",
        {
          shouldBeIncluded: true,
          action: { handler: (): void => this.actionHandlers.copyUrl(this.context.metric.id, "metric") },
          isEndOfSection: true,
          isDestructive: false,
          skipFromTabOrder: true,
        },
      ],
      [
        "delete",
        {
          shouldBeIncluded: this.canDelete(),
          action: { handler: (): void => this.actionHandlers.delete(this.context.metric.id) },
          isEndOfSection: false,
          isDestructive: true,
          skipFromTabOrder: true,
        },
      ],
    ]);
  }

  private canUpdateMetric(): boolean {
    if (!this.context || !this.context.metric || !this.context.allowedActions) {
      return false;
    }

    const isMetricOverdue = this.context.metric.daysLeft < 0;

    return (
      !isMetricOverdue &&
      this.rootScope.hasPermission("ManageGoals") &&
      !this.context.metric.sourceMetricId &&
      this.context.goal.closedStatus &&
      this.context.goal.closedStatus.status !== "abandoned" &&
      (this.currentUserService.allowedActionsSync(this.context.session.access).includes("update") || this.isUserCoOwner())
    );
  }

  private canEditMetric(): boolean {
    if (!this.context || !this.context.metric || !this.context.allowedActions) {
      return false;
    }

    return (
      this.rootScope.hasPermission("ManageGoals") &&
      !isOkrAbandoned(this.context.goal.closedStatus) &&
      (this.isGoalUnlockedAndSessionOpen() || (!this.isGoalUnlockedAndSessionOpen() && this.context.session.workflow?.editableKrFields?.length > 0)) &&
      (this.currentUserService.allowedActionsSync(this.context.session.access).includes("update") || this.isUserCoOwner())
    );
  }

  private isUserCoOwner(): boolean {
    if (!this.context.metric.ownerIds) {
      return false;
    }
    return isCurrentUserOwner(
      this.context.metric.ownerIds,
      this.currentUserService.getCurrentUserShadowRoles().map((role) => role.id)
    );
  }

  private isGoalUnlockedAndSessionOpen(): boolean {
    if (!this.context.goal || !this.context.session) {
      return false;
    }

    return this.context.session.status === "open" && !this.context.goal.locked;
  }

  private canAddChildGoal(): boolean {
    return this.isGoalUnlockedAndSessionOpen() && this.currentUserService.allowedActionsSync(this.context.session.access).includes("create");
  }

  private canCascade(): boolean {
    return (
      this.accountService.featureAvailable("okrs.kr-links.cascading") &&
      canCascadeMetric(this.context.metric, {
        isGoalAbandoned: isOkrAbandoned(this.context.goal.closedStatus),
        isCascadingShouldDecreaseToEnabled: this.isCascadingShouldDecreaseToEnabled,
      })
    );
  }

  private canMove(): boolean {
    return this.canItemBeMoved() && this.hasCurrentUserUpdatePermissions();
  }

  private hasCurrentUserUpdatePermissions(): boolean {
    return this.currentUserService.allowedActionsSync(this.context.session.access).includes("update");
  }

  private canItemBeMoved(): boolean {
    return (!this.context.metric.daysLeft || this.context.metric.daysLeft >= 0) && !isOkrAbandoned(this.context.goal.closedStatus);
  }

  private canClone(): boolean {
    return (!this.context.metric.daysLeft || this.context.metric.daysLeft >= 0) && this.rootScope.hasPermission("ManageGoals");
  }

  private canDisableProgressSync(): boolean {
    return !!this.context.metric.sourceMetricId;
  }

  private canDelete(): boolean {
    return (
      this.rootScope.hasPermission("ManageGoals") &&
      this.isGoalUnlockedAndSessionOpen() &&
      this.context.allowedActions.indexOf("delete") !== -1 &&
      !isOkrAbandoned(this.context.goal.closedStatus)
    );
  }

  private getUpdateActionConfig(): ItemAction {
    let action: ItemAction;

    if (this.isA14UpdateKeyResultEnabled) {
      action = {
        handler: (): void => this.actionHandlers.update(this.context.metric.id),
      };
    } else {
      action = { state: "._updateMetric.update", params: { id: this.context.goal.id, metricId: this.context.metric.id } };
    }

    return action;
  }
}
